import React, { ReactNode, useRef, useEffect, useState } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export type ContainerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto' | 'content';
export type ContainerAlignment = 'left' | 'center' | 'right';
export type PaddingMode = 'none' | 'minimal' | 'normal' | 'comfortable' | 'spacious';

export interface FlexibleContainerProps {
  children: ReactNode;
  size?: ContainerSize;
  alignment?: ContainerAlignment;
  padding?: PaddingMode;
  className?: string;
  maxWidth?: string; // Override max-width with custom value
  minWidth?: string; // Set minimum width
  fluid?: boolean; // If true, container grows/shrinks with content
  responsive?: boolean; // Enable responsive behavior
  centerContent?: boolean; // Center content horizontally
  adaptToSidebar?: boolean; // Adapt to sidebar state
  breakout?: boolean; // Allow content to break out of container
  noGutters?: boolean; // Remove horizontal padding
}

// Hook to detect sidebar state (you'll need to implement this based on your layout)
const useSidebarState = () => {
  // This is a placeholder - implement based on your sidebar context
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(256);

  // You would typically get this from your layout context
  useEffect(() => {
    // Implementation would depend on your sidebar state management
    const checkSidebarState = () => {
      // Example implementation
      const sidebar = document.querySelector('[data-sidebar]');
      if (sidebar) {
        const isOpen = !sidebar.classList.contains('hidden');
        setSidebarOpen(isOpen);
        setSidebarWidth(isOpen ? 256 : 0);
      }
    };

    checkSidebarState();
    window.addEventListener('resize', checkSidebarState);

    return () => window.removeEventListener('resize', checkSidebarState);
  }, []);

  return { sidebarOpen, sidebarWidth };
};

const FlexibleContainer: React.FC<FlexibleContainerProps> = ({
  children,
  size = 'auto',
  alignment = 'center',
  padding = 'normal',
  className = '',
  maxWidth,
  minWidth,
  fluid = false,
  responsive = true,
  centerContent = false,
  adaptToSidebar = false,
  breakout = false,
  noGutters = false,
}) => {
  const { classes, isCompact, isNormal } = useDensity();
  const { sidebarOpen, sidebarWidth } = useSidebarState();
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);

  // Monitor content width for auto-sizing
  useEffect(() => {
    if (size !== 'content' || !containerRef.current) return;

    const updateContentWidth = () => {
      if (containerRef.current) {
        const children = containerRef.current.children;
        let maxChildWidth = 0;

        for (let i = 0; i < children.length; i++) {
          const childWidth = children[i].getBoundingClientRect().width;
          maxChildWidth = Math.max(maxChildWidth, childWidth);
        }

        setContentWidth(maxChildWidth);
      }
    };

    const resizeObserver = new ResizeObserver(updateContentWidth);
    resizeObserver.observe(containerRef.current);
    updateContentWidth();

    return () => resizeObserver.disconnect();
  }, [size]);

  // Calculate effective max-width
  const getMaxWidth = () => {
    if (maxWidth) return maxWidth;
    if (size === 'full') return '100%';
    if (size === 'content') return `${contentWidth}px`;

    // Density-aware size calculations
    const sizeMap = {
      xs: isCompact ? '320px' : '384px', // 20rem - 24rem
      sm: isCompact ? '448px' : '512px', // 28rem - 32rem
      md: isCompact ? '640px' : '768px', // 40rem - 48rem
      lg: isCompact ? '896px' : '1024px', // 56rem - 64rem
      xl: isCompact ? '1152px' : '1280px', // 72rem - 80rem
      full: '100%', // Full width
      content: 'fit-content', // Content-based sizing
      auto: 'none',
    };

    let maxW = sizeMap[size as keyof typeof sizeMap] || sizeMap.auto;

    // Adapt to sidebar if enabled
    if (adaptToSidebar && sidebarOpen && (size as ContainerSize) !== 'full') {
      const screenWidth = window.innerWidth;
      const availableWidth = screenWidth - sidebarWidth - 64; // Account for padding
      const requestedWidth = parseInt(maxW) || screenWidth;

      if (requestedWidth > availableWidth) {
        maxW = `${availableWidth}px`;
      }
    }

    return maxW;
  };

  // Calculate padding based on mode and density
  const getPaddingClasses = () => {
    if (padding === 'none' || noGutters) return '';

    const paddingMap = {
      minimal: isCompact ? 'px-2' : 'px-3',
      normal: isCompact ? 'px-4' : isNormal ? 'px-6' : 'px-8',
      comfortable: isCompact ? 'px-6' : isNormal ? 'px-8' : 'px-12',
      spacious: isCompact ? 'px-8' : isNormal ? 'px-12' : 'px-16',
    };

    return paddingMap[padding];
  };

  // Generate responsive padding classes
  const getResponsivePadding = () => {
    if (!responsive || padding === 'none' || noGutters) return getPaddingClasses();

    const basePadding = getPaddingClasses();
    const mobilePadding = isCompact ? 'px-2' : 'px-4';

    return `${mobilePadding} sm:${basePadding.replace('px-', 'px-')}`;
  };

  // Alignment classes
  const getAlignmentClasses = () => {
    const alignMap = {
      left: 'mr-auto',
      center: 'mx-auto',
      right: 'ml-auto',
    };

    return alignMap[alignment];
  };

  // Container classes
  const containerClasses = classes(
    [
      'flexible-container',
      'w-full',

      // Fluid behavior
      fluid ? 'min-w-0 flex-shrink flex-grow' : '',

      // Breakout behavior
      breakout ? 'max-w-none' : '',

      // Alignment
      getAlignmentClasses(),

      // Padding
      getResponsivePadding(),

      // Center content
      centerContent ? 'flex flex-col items-center' : '',

      className,
    ]
      .filter(Boolean)
      .join(' ')
  );

  // Container style
  const containerStyle: React.CSSProperties = {
    maxWidth: breakout ? 'none' : getMaxWidth(),
    minWidth: minWidth,

    // Smooth transitions for responsive changes
    transition: responsive ? 'max-width 0.3s ease-in-out, padding 0.3s ease-in-out' : 'none',

    // Content-based sizing
    ...(size === 'content' && {
      width: 'fit-content',
    }),
  };

  // Inner wrapper for additional layout control
  const innerWrapperClasses = classes(
    ['w-full', centerContent ? 'text-center' : ''].filter(Boolean).join(' ')
  );

  return (
    <div ref={containerRef} className={containerClasses} style={containerStyle}>
      {centerContent ? <div className={innerWrapperClasses}>{children}</div> : children}
    </div>
  );
};

// Preset configurations for common layouts
export const ContainerPresets = {
  // Main content areas
  mainContent: {
    size: 'xl' as ContainerSize,
    padding: 'normal' as PaddingMode,
    responsive: true,
    adaptToSidebar: true,
  },

  // Modal content
  modalContent: {
    size: 'md' as ContainerSize,
    padding: 'comfortable' as PaddingMode,
    centerContent: true,
    responsive: true,
  },

  // Form containers
  formContainer: {
    size: 'lg' as ContainerSize,
    padding: 'normal' as PaddingMode,
    centerContent: true,
    responsive: true,
  },

  // Dashboard widgets
  dashboardWidget: {
    size: 'auto' as ContainerSize,
    padding: 'minimal' as PaddingMode,
    fluid: true,
    noGutters: true,
  },

  // Page headers
  pageHeader: {
    size: 'full' as ContainerSize,
    padding: 'normal' as PaddingMode,
    adaptToSidebar: true,
    responsive: true,
  },

  // Narrow content (reading)
  narrowContent: {
    size: 'md' as ContainerSize,
    padding: 'comfortable' as PaddingMode,
    centerContent: true,
    responsive: true,
  },

  // Wide content (tables, lists)
  wideContent: {
    size: 'full' as ContainerSize,
    padding: 'minimal' as PaddingMode,
    adaptToSidebar: true,
    breakout: true,
  },

  // Sidebar content
  sidebarContent: {
    size: 'auto' as ContainerSize,
    padding: 'normal' as PaddingMode,
    fluid: true,
  },
};

// Higher-order component for automatic container wrapping
export const withFlexibleContainer = <P extends object>(
  Component: React.ComponentType<P>,
  containerProps: Partial<FlexibleContainerProps> = {}
) => {
  const WrappedComponent = (props: P) => (
    <FlexibleContainer {...containerProps}>
      <Component {...props} />
    </FlexibleContainer>
  );

  WrappedComponent.displayName = `withFlexibleContainer(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default FlexibleContainer;
